import { Contact as ContactModel } from '@/shared/model/contact';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Logger } from 'fsts';
import { namespace, Action } from 'vuex-class';
import Contact from './contact/contact.vue';
import { SearchParams } from '@/shared/model/searchParams';
import DeactivateDialog from './deactivate-dialog/deactivate-dialog.vue';
import FilterDialog from './filter-dialog/filter-dialog.vue';
import DocumentsConditionsDialog from './documents-conditions-dialog/documents-conditions-dialog.vue';
import searchDataFile, { SearchData, FilterContactType } from '@/shared/model/smallPayloadModels/searchData';
import { OdataItems } from '@/shared/model/OdataItems';
import { GspUserRole, UserActiveServices } from '@/shared/model/gspUserRole';
import { GspUserRoleDetailInner } from '@/shared/model/gspUserRoleDetail';
import { CompanyZrResult } from '@/shared/model/company';
import GeneralUtils, { removeDuplicates } from '@/shared/utils/generalUtils';
import { GspRole } from '@/shared/model/gspRole';
import {
  getContactRouteType,
  getContactRouteTypeNumber,
  ContactRouteType,
} from '@/shared/utils/contactRouteUtils';

const logger = new Logger('contact-list');
const contactModule = namespace('contact');
const companyModule = namespace('company');
const authModule = namespace('auth');
const gspRoleModule = namespace('gspRole');
const gspServiceModule = namespace('gspService');
const gspUserRoleModule = namespace('gspUserRole');

@Component({
  components: {
    Contact,
    DeactivateDialog,
    FilterDialog,
    DocumentsConditionsDialog,
  },
})
export default class ContactList extends Vue {
  @authModule.Action('canSeeAll')
  private actionCanSeeAll!: any;
  @authModule.Action('addInAzureHausAdminGroup')
  private actionAddInAzureHausAdminGroup!: any;
  @authModule.Getter('isUserInAzureAdminGroup')
  private isUserInAzureAdminGroup!: any;
  @authModule.Getter('isUserSupportOrSuperAdmin')
  private isUserSupportOrSuperAdmin!: any;
  @authModule.Getter('isUserSuperAdmin')
  private isUserSuperAdmin!: any;

  @companyModule.Action('getCompanys')
  private actionGetCompanys!: any;

  @contactModule.Action('getContacts')
  private actionGetContacts!: any;
  @contactModule.Action('GetInternContacts')
  private actionGetInternContacts!: any;
  @contactModule.Action('getCompanyZr')
  private actionGetCompanyZrNums!: any;
  @contactModule.Action('getContactsExcelReport')
  private actionGetContactsExcelReport!: any;
  @contactModule.Action('updateContactAdditionalInfo')
  private actionUpdateContactAdditionalInfo!: any;
  @contactModule.Action('updateSearchData')
  private actionUpdateSearchData!: any;
  @contactModule.Getter('getContacts')
  private getContacts!: any;
  @contactModule.Getter('getContactsIsLoading')
  private contactsIsLoading!: boolean;
  @contactModule.Getter('getContactsSearchParams')
  private contactsSearchParams!: SearchParams;
  @contactModule.Getter('getSearchData')
  private getSearchData!: SearchData;

  @gspRoleModule.Action('getGspRoles')
  private actionGetGspRoles!: any;
  @gspRoleModule.Getter('getGspRoles')
  private gspRoles!: OdataItems<GspRole>;

  @gspServiceModule.Action('getGspServicesForContacts')
  private actionGetGspServices!: any;
  @gspServiceModule.Getter('getGspServicesForContacts')
  private gettterGspServices!: any;

  @contactModule.Getter('getCompanyZrsIsLoading')
  private companyZrsIsLoading!: any;
  @contactModule.Action('updateCompanyZrIsLoading')
  private updateCompanyZrIsLoading!: any;
  @contactModule.Action('updateContactsIsLoading')
  private updateContactsIsLoading!: any;
  get userHasCompany() {
    return this.сompanyZrs.length > 0;
  }
  get userCompanyLoaded() {
    return this.companyZrsIsLoading === false;
  }

  get isFilterUsed() {
    return this.getSearchData && (this.getSearchData.isActive !== null || this.getSearchData.isMeinVme !== null);
  }

  get routeContactType(): ContactRouteType {
    return getContactRouteType(this.$route.path);
  }

  get routeContactTypeNumber(): number {
    return getContactRouteTypeNumber(this.routeContactType);
  }

  // TODO(GSP-108): load every distinct user role on open the contact later
  @gspUserRoleModule.Action('getGspUserRoles')
  private actionGetGspUserRoles!: any;
  @gspUserRoleModule.Getter('getGspUserRoles')
  private gspUserRoles!: OdataItems<GspUserRole>;

  @gspUserRoleModule.Action('getUserActiveServices')
  private actionUserActiveServices!: any;
  @gspUserRoleModule.Getter('getUserActiveServices')
  private getUserActiveServices!: UserActiveServices[];

  @gspUserRoleModule.Getter('getGspUserRolesActiveAndNot')
  private gspUserRolesActiveAndNot!: OdataItems<GspUserRole>;
  @gspUserRoleModule.Getter('getGspUserRolesAllForAdmin')
  private gspUserRolesAllForAdmin!: OdataItems<GspUserRole>;
  @gspUserRoleModule.Getter('getGspUserRolesIsLoading')
  private gspUserRolesIsLoading!: any;

  @contactModule.Getter('getSearchData')
  private searchData!: SearchData;

  @Watch('searchData', { deep: true })
  @Watch('searchData.contactIdsString') // (AD-173) need it for `Watcher` to see the change in `SearchData` when filter by `Role`, !! otherwise filter by `Role` is not working
  @Watch('searchData.companyZrString') // (AD-173) see comment above
  @Watch('searchData.contactTypesString') // (AD-173) see comment above
  @Watch('contactsSearchParams.filter', { deep: true })
  public async onOptionsFilterChanged(newVal: SearchData, oldVal: SearchData) {
    // console.log('onOptionsFilterChanged newVal :>> ', newVal);
    // console.log('onOptionsFilterChanged oldVal :>> ', oldVal);
    // console.log('newVal :>> ', newVal);
    // console.log('object :>> ', newVal == oldVal);

    // (AD-158) prevent extra requests when switch between `Benutzer/Lieferanten` via left-menu
    if (this.isInitialRequest) {
      return;
    }
    // (AD-151) avoid extra queries for `Contacts`
    if (GeneralUtils.areObjectsEqual(newVal, oldVal)) {
      console.log('onOptionsFilterChanged ObjectsEqual :>> ');
      return;
    }

    logger.debug(`----filter:${oldVal}->${newVal}`);
    this.contactsSearchParams.dataOption.page = 1;
    const payload = {
      searchParams: this.contactsSearchParams,
      searchData: {
        isActive: newVal.isActive || this.searchData.isActive, // (AD-92) when filter by searchTerm in `text-field` take default value from Getter (then newVal is text value from `searchTerm` )
        isMeinVme: newVal.isMeinVme || this.searchData.isMeinVme, //this.companyZrNumbers || [],
        companyZr: newVal.companyZr || this.searchData.companyZr, //this.companyZrNumbers || [],
        isIntern: this.searchData.isIntern,
        contactIds: newVal.contactIds || this.searchData.contactIds,
        contactTypes: newVal.contactTypes || this.searchData.contactTypes,
        contact_Type: newVal.contact_Type || this.searchData.contact_Type,
      },
    };

    await this.actionGetContacts(payload)
      .then(async (result: any) => {
        const contactIds = result?.value.map((x: any) => x.recordID);

        const payload = {
          contactIds: contactIds,
          isUserSupportOrSuperAdmin: this.isUserSupportOrSuperAdmin,
        };
        await this.getGspUserRoles(payload);
        let typeContact: number = this.routeContactTypeNumber;
        const payloadServices = {
          contactIds: contactIds,
          contactType: typeContact,
        };
        await this.actionUserActiveServices(payloadServices);
        let contactType: string = this.routeContactType;
        await this.getCompanyZrsFromBackend(contactType);
      })
      .catch((err: any) => {
        logger.error(err);
      });
  }

  private pagination: {
    page: number;
    itemsPerPage: number;
    pageStart: number;
    pageStop: number;
    pageCount: number;
    itemsLength: number;
  } = { page: 1, itemsPerPage: 50, pageStart: 1, pageStop: 0, pageCount: 0, itemsLength: 0 };
  // pageStart is start of page on the X page, pageStop is the end of page on the X page like `151-200 von 4720` for the 4th page with the `itemsPerPage: 50` in the footer text

  private previousSearchParam: any = {};
  private async contactsUpdateOptions(newVal: any, oldVal: any) {
    console.log('newVal contactsUpdateOptions :>> ', newVal);
    console.log('oldVal :>> ', oldVal); // !! `oldVal` always `undefined`

    // (AD-151) avoid extra queries for `Contacts`
    if (GeneralUtils.areObjectsEqual(this.previousSearchParam, newVal)) {
      return;
    }
    this.previousSearchParam = Object.assign(newVal);

    this.contactsSearchParams.dataOption.page = newVal.page;
    this.contactsSearchParams.dataOption.itemsPerPage = newVal.itemsPerPage;
    // this.employeesSearchParams.filter = newVal.filter;
    if (
      newVal != oldVal &&
      this.contactsIsLoading === false &&
      this.isUserInAzureAdminGroup != undefined &&
      !this.isInitialRequest
    ) {
      this.getContactsSearch();
      let contactType: string = this.routeContactType;
      await this.getCompanyZrsFromBackend(contactType);
    }
  }

  private searchTerm = '';
  private companyZrNumbers = '';
  private searchByGspRoleIds: number[] = [];
  private searchByContactTypes: FilterContactType[] = [];
  searchCompanyZr = '';
  searchGspRole = '';
  сompanyZrs: CompanyZrResult[] = [];

  private resetSearchCompanyZr(): void {
    this.searchCompanyZr = '';
  }
  private resetSearchGspRole(): void {
    this.searchGspRole = '';
  }

  clearSearchCompanyZrInput(): void {
    this.searchData.companyZr = [];
    this.resetSearchCompanyZr();
  }
  clearSearchGspRoleInput(): void {
    this.searchData.contactIds = null;
    this.resetSearchGspRole();
  }

  private onBlurSearchCompanyZr(): void {
    this.resetSearchCompanyZr();
  }
  private onBlurSearchGspRole(): void {
    this.resetSearchGspRole();
  }

  private onSearchCompanyZrUpdated(): void {
    this.searchData.companyZrString = this.searchData.companyZr?.join(',');
  }

  private onSearchByGspRoleUpdated(): void {
    if (this.searchByGspRoleIds.length == 0) {
      this.searchData.contactIds = null;
      // (AD-173) need random value to trigger watcher when clear all Roles in autocomplete
      this.searchData.contactIdsString = Math.floor(Math.random() * 100000) + '';
      return;
    }

    // Search for gspUser roles that include at least one of the gsp roles selected in the filter.
    // This is possible because all GspUserRoles are cached on the frontend.
    let filterFunction = (gspUserRole: GspUserRole) => {
      if (!gspUserRole.contactId) {
        return false;
      }

      for (let gspRoleId of this.searchByGspRoleIds) {
        let searchResult = gspUserRole.gspUserRoleDetails.find((x) => {
          let roleDetails = x.roleDetails as GspUserRoleDetailInner[];
          if (roleDetails == undefined) return false;

          let searchResultInner1 = roleDetails.find((y) => {
            let moduleRoles = y.moduleRoles;
            if (moduleRoles == undefined) return false;

            let searchResultInner2 = moduleRoles.find((z) => {
              let roles = z.roles;
              if (roles == undefined) return false;

              return roles.includes(gspRoleId);
            });

            return searchResultInner2 != undefined;
          });

          return searchResultInner1 != undefined;
        });

        if (searchResult != undefined) {
          return true;
        }
      }

      return false;
    };
    // Select all ids of contacts that include one of the roles selected in the filter.
    if (this.isUserSupportOrSuperAdmin) {
      let contactIds = this.gspUserRolesAllForAdmin.items.filter(filterFunction).map((x) => x.contactId!);
      this.searchData.contactIds = contactIds;
      this.searchData.contactIdsString = contactIds.join(','); // (AD-173) need it for `Watcher` to see the change in `SearchData` when filter by `Role` since above `contactIds` is Reference type and has the SAME value in `Old/New` values when trigger `Watcher`
    } else {
      let contactIds = this.gspUserRolesActiveAndNot.items.filter(filterFunction).map((x) => x.contactId!);
      this.searchData.contactIds = contactIds;
      this.searchData.contactIdsString = contactIds.join(','); // (AD-173) need it for `Watcher` to see the change in `SearchData` when filter by `Role` since above `contactIds` is Reference type and has the SAME value in `Old/New` values when trigger `Watcher`
    }
  }

  private onSearchByContactTypeUpdated() {
    if (this.searchByContactTypes.length == 0) {
      this.searchData.contactTypes = null;
      // (AD-173) need random value to trigger watcher when clear all ContactTypes in autocomplete
      this.searchData.contactTypesString = Math.floor(Math.random() * 100000) + '';
      return;
    }

    this.searchData.contactTypes = this.searchByContactTypes;
    this.searchData.contactTypesString = this.searchData.contactTypes?.join(',');
  }

  async created() {
    this.isInitialRequest = true;
    this.updateContactsIsLoading(undefined); // (AD-158) prevent extra requests when switch between `Benutzer/Lieferanten` via left-menu
    this.actionUpdateSearchData(searchDataFile.defaultData());
    // console.log('CREATED this.searchData :>> ', Object.assign(this.searchData));
    this.updateCompanyZrIsLoading(undefined); // (AD-158) fix blinking `Kein Firma` message when switch between `Benutzer/Lieferanten`
    this.contactsSearchParams.filter = '';
    await this.initializeDataLoading();
  }

  private async initializeDataLoading(): Promise<void> {
    try {
      // Verify Azure admin group access with retry
      await this.loadLogicWithRetry();

      // Load Azure admin group specific data (only if user has access)
      if (this.isUserInAzureAdminGroup) {
        await this.loadAzureAdminGroupData();
      }
    } catch (error) {
      logger.error('Error during data loading (`initializeDataLoading`):', error);
      this.isInitialRequest = false;
    }
  }

  private async loadAzureAdminGroupData(): Promise<void> {
    // Determine contact type from route for filtering
    let contactType: string = this.routeContactType;

    await this.getCompanyZrsFromBackend(contactType); // (AD-145) load 1st to avoid long loading all other data, otherwise see progress bar
    await this.actionGetGspRoles({ contactType: contactType });

    // (AD-158) load services before contacts to prevent show wrong data for `Benutzer/Lieferanten` after switch
    let contactServicesType: string = this.routeContactType;

    await this.actionGetGspServices({ contactType: contactServicesType });

    // Load contacts data and process results
    await this.getContactsData()
      .then(async (result) => {
        console.log('result get ContactsData:>> ', result);
        this.isInitialRequest = false;
        let searchDataNewObj = Object.assign({}, this.searchData);
        const contactIds = result?.value.map((x: any) => x.recordID);

        const payload: any = {
          contactIds: contactIds,
          isUserSupportOrSuperAdmin: this.isUserSupportOrSuperAdmin,
          isInitialRequest: searchDataNewObj.isInitialRequest,
        };

        // Determine contact type number for payload
        let typeContact: number = this.routeContactTypeNumber;
  
        const payloadServices = {
          contactIds: contactIds,
          contactType: typeContact,
        };

        await this.actionUserActiveServices(payloadServices);

        // Load GSP user roles with admin-specific logic
        await this.getGspUserRoles(payload)
          .then((result: any) => {
            // (AD-139) for Admin load all records to filter among all records
            if (this.isUserSupportOrSuperAdmin) {
              payload.isGetAll = true;
              this.getGspUserRoles(payload);
            }
          })
          .catch((err) => {
            logger.error(err);
          });
      })
      .catch((err) => {
        this.isInitialRequest = false;
        logger.error(err);
      });
  }

  isInitialRequest = true;
  mounted() {
    if (this.$route.fullPath.includes('/suppliers')) {
      this.searchData.contact_Type = 'supplier';
      this.searchData.isIntern = false;
    } else if (this.$route.fullPath.includes('/intern')) {
      this.searchData.isIntern = true;
    } else {
      this.searchData.contact_Type = '';
      this.searchData.isIntern = false;
    }
  }

  /**
   * Retry logic for actionCanSeeAll with exponential timeout between attempts. This is to handle potential transient issues with Azure group status retrieval on component load.
   * @param maxRetries - Maximum number of retry attempts (default: 3)
   */
  private async loadLogicWithRetry(maxRetries = 3): Promise<void> {
    let lastError: any;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        // Attempt to get admin group status from Azure
        await this.actionCanSeeAll();
        return; // if success, then exit the retry loop
      } catch (err: any) {
        lastError = err;
        logger.warn(`canSeeAll attempt ${attempt}/${maxRetries} failed:`, err);

        // Exponential timeout: 2s, 4s, 6s...
        if (attempt < maxRetries) {
          await new Promise((resolve) => setTimeout(resolve, 2000 * attempt));
          continue;
        }

        logger.error('canSeeAll failed after all retries:', lastError);
        throw lastError;
      }
    }
  }

  async getGspUserRoles(payload: any) {
    await this.actionGetGspUserRoles(payload)
      .then((result: any) => {
        logger.log('result get-GspUserRoles :>> ', result);
      })
      .catch((err: any) => {
        logger.error(err);
      });
  }
  async getCompanyZrsFromBackend(contactType: string) {
    const payload = {
      isInSuperAdminOrSupportGroup: false,
      contactType: contactType,
      IsActive: this.searchData.isActive,
      isIntern: this.searchData.isIntern,
    };
    await this.actionGetCompanyZrNums(payload)
      .then((result: CompanyZrResult[]) => {
        console.log('----------------------actionGetCompanyZrNums--------------------------------------  ===>>>result :>> ', result); 
        logger.log('result :>> ', result);
        this.сompanyZrs = result;
      })
      .catch((err: any) => {
        logger.error(err);
      });
  }

  private async getContactsData() {
    // don't take getter value in this case
    return await this.actionGetContacts({ searchParams: this.contactsSearchParams, searchData: this.searchData })
      .then(async (result: any) => {
        logger.log('result :>> ', result);
        return result;
      })
      .catch((err: any) => {
        logger.error(err);
      });
  }

  private changeSearch(searchTerm: string) {
    if (searchTerm.length < 2) return;
    this.searchContacts(searchTerm);
  }
  private changeSearchInput(value: string) {
    if (value.length == 0) {
      this.resetSearch();
    }
  }
  private resetSearch() {
    const emptyString = '';
    this.searchContacts(emptyString);
  }

  private async searchContacts(filterValue: string) {
    this.contactsSearchParams.filter = filterValue; // `this.get ContactsSearch` will be called via `@Watch`
    // if (!this.contactsIsLoading) this.get ContactsSearch();
  }
  private async getContactsSearch() {
    await this.actionGetContacts({ searchParams: this.contactsSearchParams, searchData: this.searchData })
      .then(async (result: any) => {
        const contactIds = result?.value.map((x: any) => x.recordID);

        const payload = {
          contactIds: contactIds,
          isUserSupportOrSuperAdmin: this.isUserSupportOrSuperAdmin,
        };
        await this.getGspUserRoles(payload);
        let typeContact: number = 1;
        if (this.$route.fullPath.includes('/suppliers')) {
          typeContact = 2;
        } else if (this.$route.fullPath.includes('/intern')) {
          typeContact = 3;
        }
        const payloadServices = {
          contactIds: contactIds,
          contactType: typeContact,
        };
        await this.actionUserActiveServices(payloadServices);
        let contactType: string = this.routeContactType;
        await this.getCompanyZrsFromBackend(contactType);
      })
      .catch((err: any) => {
        logger.error(err);
      });
  }

  get filterContactTypes() {
    return Object.values(FilterContactType).map((x) => ({ value: x, text: this.$t(`filterContactTypes.${x}`) }));
  }

  get sortedGspRoles() {
    let gspRoles = [...this.gspRoles.items];

    gspRoles.sort((gspRole1, gspRole2) => {
      let serviceName1 = removeDuplicates(gspRole1.rolesData.map((x) => x.service.name)).join(',');
      let serviceName2 = removeDuplicates(gspRole2.rolesData.map((x) => x.service.name)).join(',');

      if (serviceName1 > serviceName2) return 1;
      if (serviceName2 > serviceName1) return -1;

      if (gspRole1.name > gspRole2.name) return 1;
      if (gspRole2.name > gspRole1.name) return -1;

      return 0;
    });

    return gspRoles;
  }

  //#region Sorting and Expand/collapse buttons logic
  private isExpandAll = false;

  get IsSortAscending(): boolean {
    return this.contactsSearchParams.dataOption.sortDesc === false;
  }

  private sortContacts() {
    this.contactsSearchParams.dataOption.sortDesc = !this.contactsSearchParams.dataOption.sortDesc;
    this.getContactsSearch();
  }

  private getGspRoleLabel(gspRole: GspRole): string {
    if (!gspRole) return '';
    if (!gspRole.rolesData?.length) return gspRole.name;
    return `${removeDuplicates(gspRole.rolesData.map((x) => x.service.name)).join(',')} - ${gspRole.name}`;
  }

  private expandCollapseAllContacts() {
    let expansions = document.querySelectorAll<HTMLElement>(
      '.v-expansion-panel-header:not(.v-expansion-panel-header--active)'
    );
    if (this.isExpandAll) {
      expansions = document.querySelectorAll<HTMLElement>('.v-expansion-panel-header.v-expansion-panel-header--active');
    }

    expansions.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.click();
      }
    });
    this.isExpandAll = !this.isExpandAll;
  }
  //#endregion

  deactivateUser(contact: ContactModel): void {
    logger.log('(this.isUserSupportOrSuperAdmin :>> ', this.isUserSuperAdmin);
    if (contact.isActive || this.isUserSuperAdmin) {
      this.dialogDeactivate.model = contact;
      this.showDeactivateDialog();
    }
  }

  async changeHouseAdminStatusOfUser(payload: {
    contact: ContactModel;
    newValue: boolean;
    previousValue: boolean;
    onSuccess: (newValue: boolean) => void;
    onError: (previousValue: boolean) => void;
  }) {
    // console.log('payload :>> ', payload);
    const { contact, newValue, previousValue, onSuccess, onError } = payload;

    try {
      // Validate BEFORE making any changes
      if (!contact.contactAzureId) {
        logger.error('Cannot add contact in Azure group if `Contact` does not have `contactAzureId` field');
        onError(previousValue);
        return;
      }

      const result = await this.actionUpdateContactAdditionalInfo({
        ...contact,
        isHouseAdmin: newValue,
      });
      const requestPayload = {
        azureUserId: contact.contactAzureId,
        isHouseAdmin: result.result.isHouseAdmin,
      };
      logger.log(' addInAzureHausAdminGroup requestPayload :>> ', requestPayload);

      contact.isHouseAdmin = result.result.isHouseAdmin;

      try {
        await this.actionAddInAzureHausAdminGroup(requestPayload); // NO result (response 204 `No Content`) both for add/remove Azure User in Azure Group // https://learn.microsoft.com/en-us/graph/api/group-post-members
        onSuccess(result.result.isHouseAdmin);
      } catch (azureErr: any) {
        logger.error('zzz actionAddInAzureHausAdminGroup ERR :>> ', azureErr);

        // Revert backend change if Azure fails
        contact.isHouseAdmin = previousValue;
        contact.isSkipLog = true;
        contact.removeLogId = result.result.logId; // to remove log entry for reverted change in `updateContactAdditionalInfo`
        await this.actionUpdateContactAdditionalInfo(contact);
        throw azureErr; // Re-throw to outer catch
      }
    } catch (err: any) {
      logger.error('Backend error:', err);

      contact.isHouseAdmin = previousValue;
      // Call error callback - child reverts its own state
      onError(previousValue);
      // if Backend failed - show error message
      // this.$message.open(
      //   this.$tc('error_title'),
      //   this.$tc('update_failed_text'),
      //   { hasCancelBtn: false }
      // );

      // ✅ DON'T update switch - it stays in original state
      // Because we used :input-value, it never changed
    }
  }

  //#region Deactivate-dialog
  dialogDeactivate = {
    show: false,
    model: {},
    OnClose: (): void => {
      this.dialogDeactivate.show = false;
    },
  };

  showDeactivateDialog(): void {
    logger.log('show-deactivate-dialog');
    this.dialogDeactivate.show = true;
  }

  dialogDeactivateClose(deactivatedId?: number): void {
    this.dialogDeactivate.show = false;
    if (deactivatedId) {
      this.handleUserDeactivation(deactivatedId);
    }
  }

  async handleUserDeactivation(recordId: number) {
    // console.log('zz handleUserDeactivation :>> ', recordId);
    const contact = this.getContacts.items.find((c: ContactModel) => c.recordID.toString() === recordId.toString());

    // Only proceed if the user was actually a House Admin
    if (contact && contact.isHouseAdmin) {
      // Construct the complex payload required by changeHouseAdminStatusOfUser
      const payload = {
        contact: contact,
        newValue: false, // force removal
        previousValue: true,
        onSuccess: (finalValue: boolean) => {
          contact.isHouseAdmin = finalValue;
          logger.log(`zz House Admin removed for deactivated user: ${contact.email}`);
        },
        onError: (revertValue: boolean) => {
          // If this fails, the user is deactivated but still an Admin in DB/Azure
          logger.error(` Failed to remove House Admin status during deactivation`);
        },
      };

      await this.changeHouseAdminStatusOfUser(payload);
    }
  }

  //#endregion

  //#region Filter-dialog
  dialogFilter = {
    show: false,
    model: {},
    OnClose: () => {
      this.dialogFilter.show = false;
    },
  };

  showFilterDialog() {
    logger.log('show-filter-dialog');
    this.dialogFilter.show = true;
  }

  showFilter() {
    this.dialogFilter.show = true;
  }
  //#endregion

  isLoadingFile = false;
  downloadExcel() {
    const payload = {
      searchParams: this.contactsSearchParams,
      searchData: {
        isActive: this.searchData.isActive,
        companyZr: this.searchData.companyZr,
        isIntern: this.searchData.isIntern,
        isMeinVme: this.searchData.isMeinVme,
        contactIds: this.searchData.contactIds,
        contactTypes: this.searchData.contactTypes,
        lang: this.$i18n.locale,
        contact_Type: this.searchData.contact_Type,
      },
    };

    this.isLoadingFile = true;
    this.actionGetContactsExcelReport(payload).finally(() => {
      this.isLoadingFile = false;
    });
  }

  //#region Documents-conditions-dialog
  dialogDocumentConditions: any = {
    show: false,
    model: {},
    OnClose: () => {
      this.dialogDocumentConditions.show = false;
    },
    UpdateContactPanelId: (id: any) => {
      this.updateModel(id);
    },
  };

  updateModel(id: any) {
    logger.log('panel id :>> ', id);
    this.dialogDocumentConditions.model.contactPanelId = id;
  }

  showDocumentConditionsDialog(contact: ContactModel): void {
    logger.log('show-filter-dialog');
    this.dialogDocumentConditions.model = contact;
    this.dialogDocumentConditions.show = true;
  }

  get titleSearch() {
    if (this.$route.fullPath.includes('/suppliers')) {
      return this.$t('searchSuppliers_label');
    } else {
      return this.$t('search_label');
    }
  }

  get titleCount() {
    if (this.$route.fullPath.includes('/suppliers')) {
      return this.$t('contactsSuppliers_count', { 0: this.getContacts.total });
    } else {
      return this.$t('contacts_count', { 0: this.getContacts.total });
    }
  }

  get loadingContacts() {
    if (this.$route.fullPath.includes('/suppliers')) {
      return this.$t('loadingSuppliers_contacts');
    } else {
      return this.$t('loading_contacts');
    }
  }

  //#endregion
}
